import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import _, { cloneDeep } from 'lodash'

import {
  TextField,
  Tooltip,
  FormLabel,
  Alert,
  FormControlLabel,
  Divider,
  FormGroup,
  Checkbox,
} from '@mui/material'

import { Info } from '@mui/icons-material'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'
import { hasAnswerChanged } from 'utils/functions'
import AddAttachments from 'components/AddAttachments'
import { useSelector } from 'react-redux'
import {
  getAddressInfo,
  getAddressOcrData,
  postRequestAddressOcr,
  postSkipAddressOcr,
} from 'services/apiKYP'
import { formatToCEP } from 'brazilian-values'

import { reportError } from 'utils/errorReporter'
import { DEFAULT_ADDRESS_PROOF_THIRD_PARTY_TYPE } from 'utils/constants/addressOcr'
import LoadingBackdrop from 'components/LoadingBackdrop'
import styles from './styles'
import QuestionButton from '../QuestionButton'
import AddressOcrErrorMessage from '../AddressOcrErrorMessage'
import AddressOcrValidationReport from '../AddressOcrValidationReport'
import AddressOcrNameForm from '../AddressOcrNameForm'

const initialState = {
  neighborhood: '',
  zipcode: '',
  city: '',
  address: '',
  state: '',
  complement: '',
  house_number: '',
  is_address_proof_on_person_name: true,
  third_party_name_on_address_proof: '',
  third_party_relation_on_address_proof: DEFAULT_ADDRESS_PROOF_THIRD_PARTY_TYPE,
  attachments: [],
}

const initialErrors = {
  neighborhood: false,
  zipcode: false,
  city: false,
  address: false,
  state: false,
  house_number: false,
  attachments: false,
  third_party_name_on_address_proof: false,
  third_party_relation_on_address_proof: false,
}

export default function Address(props) {
  const { questionData, saveResponses, formOwner } = props
  const classes = styles()

  const form_uuid = useSelector((state) => state.form.uuid)

  const token = useSelector((state) => state.auth.access_token)

  const [formState, setFormState] = useState(
    questionData.has_answer ? questionData.answer : initialState,
  )

  const [formErrors, setFormErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)
  const [ocrLoading, setOcrLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData?.has_answer)
  const [latestAttachment, setLatestAttachment] = useState({})
  const [ocrData, setOcrData] = useState({})
  const [ocrDataErrorCode, setOcrDataErrorCode] = useState(null)
  const [skipOcrDataChecked, setSkipOcrDataChecked] = useState(false)
  const [ocrSentSucessfuly, setOcrSentSuccesfully] = useState(false)

  const handleAutoFillForm = (zipcode) => {
    getAddressInfo(token, zipcode)
      .then((response) => {
        const addressData = response.data
        const newFormState = cloneDeep(formState)
        Object.keys(addressData).forEach((key) => {
          if (key !== 'zipcode') {
            newFormState[key] = addressData[key]
          }
        })
        setFormState(newFormState)
      })
      .catch(() => {
        toast.error(
          'Não foi possível recuperar os dados do endereço pelo CEP, preencha manualmente o restante dos campos',
        )
      })
  }

  const handleFieldChange = (event) => {
    const key = event.target.id
    const { value } = event.target
    const newFormState = cloneDeep(formState)
    newFormState[key] = key !== 'zipcode' ? value : formatToCEP(value)
    setFormState(newFormState)
  }

  const isResponseValid = () => {
    const newFormErrors = cloneDeep(initialErrors)
    let validResponse = true
    Object.keys(formErrors).forEach((key) => {
      if (key === 'attachments' && formState[key].length === 0) {
        validResponse = false
        newFormErrors[key] = true
      } else if (
        ['third_party_name_on_address_proof', 'third_party_name_on_address_proof'].includes(key)
      ) {
        newFormErrors[key] = true
        if (formState.is_address_proof_on_person_name) {
          newFormErrors[key] = false
        } else {
          newFormErrors[key] = _.isEmpty(formState[key])
        }
        validResponse = newFormErrors[key] ? false : validResponse
      } else if (formState[key] === '' || formState[key] === undefined) {
        validResponse = false
        newFormErrors[key] = true
      }
    })
    setFormErrors(newFormErrors)
    return validResponse
  }

  const callSaveResponse = (ocrResponse) => {
    const newQuestionData = cloneDeep(questionData)

    newQuestionData.has_answer = true

    newQuestionData.answer = formState
    newQuestionData.answer.ocr_data_uuid = ocrResponse.uuid

    saveResponses(
      newQuestionData,
      'address',
      !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
    )
      .then(() => {
        setSaved(true)
        setEdited(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const requestOcrScanApi = async () => {
    setOcrLoading(true)
    postRequestAddressOcr(token, {
      attachment_uuid: latestAttachment?.uuid,
      form_uuid: form_uuid,
      street_address: formState.address,
      zip_code: formState.zipcode,
      [formState.third_party_relation_on_address_proof]:
        formState.third_party_name_on_address_proof,
    })
      .then((response) => {
        setOcrData(response.data)
        setOcrSentSuccesfully(response.data?.latest_try?.is_valid)
        callSaveResponse(response.data)
      })
      .catch((error) => {
        setLoading(false)
        reportError(error)
        setOcrDataErrorCode(error?.response?.data?.code || 'unknow')
        setOcrSentSuccesfully(false)
      })
      .finally(() => {
        setOcrLoading(false)
      })
  }

  const requestOcrSkipApi = async () => {
    try {
      const response = await postSkipAddressOcr(token, form_uuid, latestAttachment?.uuid)
      setOcrData(response.data)
      setOcrSentSuccesfully(true)
      callSaveResponse(response.data)
    } catch (error) {
      setLoading(false)
      reportError(error)
      setOcrSentSuccesfully(false)
      setOcrDataErrorCode(error?.response?.data?.code || 'unknow')
    }
  }

  const validateAndSave = async () => {
    if (!isResponseValid()) {
      toast.error('Verifique o preenchimento de todos os campos')
      return
    }
    setLoading(true)
    setOcrSentSuccesfully(false)
    setOcrDataErrorCode(null)

    if (!skipOcrDataChecked) {
      await requestOcrScanApi()
    } else {
      await requestOcrSkipApi()
    }
  }
  const handleUpdateAttachments = (attachmentList) => {
    const newFormState = cloneDeep(formState)
    newFormState.attachments = attachmentList
    setLatestAttachment(attachmentList[0])
    setFormState(newFormState)
  }

  const handleCheckSkipOcr = (ev) => {
    setEdited(true)
    setSkipOcrDataChecked(ev.target.checked)
  }

  useEffect(() => {
    const loadOcrData = () => {
      if (!form_uuid) {
        return
      }

      getAddressOcrData(token, form_uuid)
        .then((response) => {
          setOcrData(response.data)
          setLatestAttachment(response.data?.final_attachment)
        })
        .catch((error) => {
          reportError(error)
        })
    }
    loadOcrData()
  }, [form_uuid])

  useEffect(() => {
    const populateOcrDataFields = () => {
      setSkipOcrDataChecked(ocrData?.continued_without_validation || false)
    }

    if (!_.isEmpty(ocrData)) {
      populateOcrDataFields()
    }
  }, [ocrData])

  useEffect(() => {
    if (questionData?.has_answer) {
      setFormState({ ...formState, ...questionData?.answer })
    } else {
      setFormState(initialState)
    }
    setSaved(questionData?.has_answer)
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged([questionData?.answer], [formState]) && saved) {
      setEdited(true)
    }
  }, [formState])

  useEffect(() => {
    if (formState.zipcode.length === 9 && hasAnswerChanged([questionData?.answer], [formState])) {
      handleAutoFillForm(formState.zipcode)
    }
  }, [formState.zipcode])

  const hasToShowSkipOcrCheckBox = () => {
    if (ocrData?.continued_without_validation) {
      return true
    }
    return !ocrData?.latest_try?.is_valid && ocrData?.total_tries >= 1
  }

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Endereço</h3>
        <div className={classes.form}>
          <FormLabel>
            Informe aqui seu endereço residencial:{' '}
            <Tooltip title="Ao digitar seu CEP completo seu endereço será carregado, porém atente-se a verificar que todos os campos foram preenchidos">
              <Info fontSize="small" color="info" />
            </Tooltip>
          </FormLabel>

          <div className={classes.formFields}>
            <TextField
              id="zipcode"
              label="CEP"
              onChange={handleFieldChange}
              value={formState.zipcode}
              error={formErrors.zipcode}
              size="small"
              required
              inputProps={{ maxLength: 9 }}
            />
            <TextField
              id="state"
              label="Estado"
              onChange={handleFieldChange}
              value={formState.state}
              error={formErrors.state}
              size="small"
              required
            />
            <TextField
              id="city"
              label="Cidade"
              onChange={handleFieldChange}
              value={formState.city}
              error={formErrors.city}
              size="small"
              required
            />
            <TextField
              id="neighborhood"
              label="Bairro"
              onChange={handleFieldChange}
              value={formState.neighborhood}
              error={formErrors.neighborhood}
              size="small"
              required
            />
            <TextField
              id="address"
              label="Endereço"
              onChange={handleFieldChange}
              value={formState.address}
              error={formErrors.address}
              size="small"
              required
            />
            <TextField
              id="house_number"
              label="Numero"
              onChange={handleFieldChange}
              value={formState.house_number}
              error={formErrors.house_number}
              size="small"
              required
            />
            <TextField
              id="complement"
              label="Complemento"
              onChange={handleFieldChange}
              value={formState.complement}
              size="small"
            />

            <Divider />

            <AddAttachments
              targetFilename={`${formOwner?.prospect?.person?.cpf}-Comprovante_de_residencia`}
              title="Comprovante de residência*"
              attachments={formState.attachments}
              updateAttachments={handleUpdateAttachments}
              error={formErrors.attachments}
              answerId={questionData?.answer_uuid}
              allowSingleFileOnly={true}
              deleteFromAnswerOnly={true}
            />

            {!edited && !_.isEmpty(formState.attachments) && (
              <React.Fragment>
                {ocrData?.continued_without_validation && (
                  <strong style={{ color: 'orange', fontSize: '1.4rem' }}>
                    Você optou por continuar sem realizar a validação do comprovante de residência. Por favor, prossiga na resolução do questionário.
                  </strong>
                )}
                {ocrData?.latest_try?.is_valid && (
                  <strong style={{ color: 'green', fontSize: '1.4rem' }}>
                    O comprovante de residência foi validado.
                  </strong>
                )}
              </React.Fragment>
            )}

            {!ocrData?.was_validated_or_skipped && (
              <Alert severity="info">
                O comprovante de residência deve ter sido emitido nos últimos 3 meses.
              </Alert>
            )}

            <AddressOcrNameForm
              formState={formState}
              updateFormState={setFormState}
              ocrData={ocrData}
              formErrors={formErrors}
            />

            {ocrDataErrorCode ? (
              <AddressOcrErrorMessage ocrErrorCode={ocrDataErrorCode} />
            ) : (
              !ocrSentSucessfuly &&
              !ocrData?.latest_try?.is_valid && <AddressOcrValidationReport ocrData={ocrData} />
            )}

            {hasToShowSkipOcrCheckBox() && (
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={skipOcrDataChecked} onChange={handleCheckSkipOcr} />}
                  label="Declaro estar ciente de que meu comprovante de residência não foi validado pelo sistema e
                   desejo prosseguir respondendo 
                  o formulário. Assumo a integridade das informações prestadas sob risco de reprovação no processo de seleção."
                />
              </FormGroup>
            )}
          </div>
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={saved && !edited}
        />
      </div>

      <LoadingBackdrop
        open={ocrLoading}
        title="Estamos validando seu comprovante de residência"
        messages={[
          'Abrindo o documento',
          'Lendo as informações',
          'Verificando os dados',
          'Finalizando...',
        ]}
      ></LoadingBackdrop>
    </div>
  )
}
